// Авто-восстановление после недогруженного чанка (ChunkLoadError).
// Также ловим падение importScripts в воркерах (ace/emmet и пр.): воркер грузит
// hashed-ассет старого билда, которого уже нет после деплоя (FRONT-1B).
//
// Такое случается, когда HTML/манифест сборки не совпадает с реально
// задеплоенными файлами (деплой во время сессии, протухшая cookie is_canary,
// кэш service worker'а). Вместо непонятного экрана ошибки перезагружаем
// страницу один раз; защита от цикла — метка времени в sessionStorage.

const RELOAD_KEY = 'chunk_error_reload_at';
const RELOAD_COOLDOWN = 10_000;

const CHUNK_ERROR_RE =
    /Loading (CSS )?chunk|ChunkLoadError|Failed to execute 'importScripts'|The script at .* failed to load/i;

// Сервер отдал HTML вместо JS-чанка (деплойная гонка: файла уже нет, nginx отдаёт
// fallback-страницу с 200). Браузер падает на парсинге ответа: Chrome/Safari —
// "Unexpected token '<'", Firefox — "expected expression, got '<'". Источник ошибки —
// URL чанка; лечится тем же одиночным релоадом (FRONT-FR).
const HTML_INSTEAD_OF_JS_RE = /Unexpected token '<'|expected expression, got '<'/i;
const CHUNK_URL_RE = /\.chunk\.js(\?|$)|_next\/static\/chunks\//i;

// Битый/пустой JS-чанк, который браузер не смог распарсить: сервер/кэш отдал
// обрезанное или мусорное тело (гонка деплоя, запаркованный SW-кэш) — падает
// SyntaxError вида "Unexpected identifier 'code'", "Unexpected token 'x'",
// "expected expression". Отличается от HTML_INSTEAD_OF_JS (там '<' и src чанка):
// у parse-ошибки чанка filename = URL страницы (embedded), поэтому ловим по
// токену + флагу embedded, чтобы не релоадить реальные баги на обычных страницах
// (FRONT-26DP/8F/FK2).
const CHUNK_PARSE_ERROR_RE =
    /Unexpected identifier|Unexpected token|Unexpected end of input|Unexpected number|expected expression|expected ';'|expected ':'|Unterminated string|malformed numeric literal/i;

// Embedded-опросы: FRONT-26DP концентрируется именно там (встроенный iframe
// testograf на чужих сайтах), где чанк переживает деплой дольше остальных.
const isEmbeddedSurvey = (): boolean => {
    if (typeof window === 'undefined') return false;

    return /(?:^|[?&])embedded(=|&|$)/.test(window.location.search);
};

const isChunkParseError = (event: ErrorEvent): boolean => {
    const message = String(event.message || '');
    const err = event.error as Error | null;

    if (!CHUNK_PARSE_ERROR_RE.test(message)) {
        return false;
    }

    // Настоящий SyntaxError (парсинг скрипта) + embedded-опрос.
    return (err?.name === 'SyntaxError' || /^SyntaxError/i.test(message)) && isEmbeddedSurvey();
};

const isHtmlInsteadOfChunk = (event: ErrorEvent): boolean => {
    const message = String(event.message || (event.error as Error | null)?.message || '');

    return HTML_INSTEAD_OF_JS_RE.test(message) && CHUNK_URL_RE.test(event.filename || '');
};

const isChunkError = (value: unknown): boolean => {
    if (!value) return false;
    if (typeof value === 'string') return CHUNK_ERROR_RE.test(value);
    if (value instanceof Error) return CHUNK_ERROR_RE.test(value.name) || CHUNK_ERROR_RE.test(value.message);

    return false;
};

const reloadOnce = (): void => {
    try {
        const now = Date.now();
        const last = Number(sessionStorage.getItem(RELOAD_KEY) || 0);

        if (now - last < RELOAD_COOLDOWN) {
            return;
        }

        sessionStorage.setItem(RELOAD_KEY, String(now));
    } catch (e) {
        // sessionStorage может быть недоступен (приватный режим) — перезагружаемся без защиты
    }

    window.location.reload();
};

export const installChunkLoadReload = (): void => {
    if (typeof window === 'undefined') return;

    window.addEventListener(
        'error',
        (event) => {
            const target = event.target as HTMLElement | null;

            if (target && target.tagName === 'SCRIPT') {
                const src = (target as HTMLScriptElement).src || '';

                if (/\.chunk\./.test(src)) {
                    reloadOnce();

                    return;
                }
            }

            if (isHtmlInsteadOfChunk(event)) {
                reloadOnce();

                return;
            }

            // Битый/пустой чанк на embedded-опросе: parse SyntaxError. Лечится
            // тем же одиночным релоадом — свежая страница перезапросит целый чанк.
            if (isChunkParseError(event)) {
                reloadOnce();

                return;
            }

            if (isChunkError(event.message) || isChunkError(event.error)) {
                reloadOnce();
            }
        },
        true,
    );

    window.addEventListener('unhandledrejection', (event) => {
        if (isChunkError(event.reason)) {
            reloadOnce();
        }
    });
};
